import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getTransactions, getTransactionStats } from '../lib/api';
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  RefreshCw, 
  Wallet, 
  Filter,
  Download,
  Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [transactionsRes, statsRes] = await Promise.all([
          getTransactions(100),
          getTransactionStats()
        ]);
        setTransactions(transactionsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        logger.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredTransactions = transactions.filter(tx => {
    if (filter === 'all') return true;
    return tx.type === filter;
  });

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownLeft className="w-4 h-4 text-emerald-500" />;
      case 'send':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'receive':
        return <ArrowDownLeft className="w-4 h-4 text-emerald-500" />;
      case 'exchange':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return <Wallet className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'deposit':
      case 'receive':
        return 'text-emerald-500';
      case 'send':
        return 'text-red-500';
      case 'exchange':
        return 'text-blue-500';
      default:
        return 'text-foreground';
    }
  };

  const formatAmount = (type, amount, currency) => {
    const prefix = type === 'send' ? '-' : '+';
    return `${prefix}$${amount.toFixed(2)} ${currency}`;
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Description', 'Amount', 'Fee', 'Currency', 'Status'];
    const rows = transactions.map(tx => [
      new Date(tx.created_at).toLocaleString(),
      tx.type,
      tx.description,
      tx.amount,
      tx.fee,
      tx.currency,
      tx.status
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions.csv';
    a.click();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="transactions-container"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold">Transaction History</h1>
            <p className="text-muted-foreground">View all your transactions</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 rounded-xl" data-testid="filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="deposit">Deposits</SelectItem>
                <SelectItem value="send">Sent</SelectItem>
                <SelectItem value="receive">Received</SelectItem>
                <SelectItem value="exchange">Exchanges</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-xl" onClick={exportToCSV} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Transactions</p>
              <p className="text-2xl font-heading font-bold" data-testid="total-transactions">
                {stats?.transaction_count || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Sent</p>
              <p className="text-2xl font-heading font-bold text-red-500" data-testid="stats-sent">
                ${stats?.total_sent?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Received</p>
              <p className="text-2xl font-heading font-bold text-emerald-500" data-testid="stats-received">
                ${stats?.total_received?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Fees Paid</p>
              <p className="text-2xl font-heading font-bold text-orange-500" data-testid="stats-fees">
                ${stats?.total_fees?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Transactions List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">
              {filter === 'all' ? 'All Transactions' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Transactions`}
              <span className="text-muted-foreground font-normal text-sm ml-2">
                ({filteredTransactions.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-16">
                <Wallet className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-lg font-medium">No transactions found</p>
                <p className="text-muted-foreground">
                  {filter === 'all' 
                    ? 'Start by making a deposit or sending money'
                    : `No ${filter} transactions yet`
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTransactions.map((tx, index) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors"
                    data-testid={`transaction-item-${tx.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                      {getTransactionIcon(tx.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{tx.description}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full bg-muted ${getTypeColor(tx.type)}`}>
                          {tx.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{new Date(tx.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{new Date(tx.created_at).toLocaleTimeString()}</span>
                        {tx.counterparty && (
                          <>
                            <span>•</span>
                            <span className="truncate">{tx.counterparty}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-mono font-semibold ${getTypeColor(tx.type)}`}>
                        {formatAmount(tx.type, tx.amount, tx.currency)}
                      </p>
                      {tx.fee > 0 && (
                        <p className="text-xs text-muted-foreground">Fee: ${tx.fee.toFixed(2)}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </Layout>
  );
};

export default Transactions;
